<template>
  <Card :class="{'no-card': noCard}">
    <div v-if="!withoutTitle" :class="titleClass">
      {{ $t(title) }}
      <sup v-if="required">*</sup>
    </div>
    <div v-if="!withoutSubTitle && !withHtmlSubTitle" class="cardSubTitle">{{ $t(subtitle) }}</div>
    <div
      v-if="!withoutSubTitle && withHtmlSubTitle"
      class="cardSubTitle"
      v-html="subtitle"
    ></div>
    <Card
      v-if="patterns.length"
      class="fileCard"
      style="background-color: rgb(var(--file-pattern-color));"
      v-for="pattern in patterns"
    >
      <div>
        <v-icon class="uploadIcon mr-2" color="primary" size="18px">mdi-file-outline</v-icon>
        <span @click="downloadPattern(pattern)" class="text">
          {{ $t(pattern.displayName ? pattern.displayName : pattern.fileName) }}</span>
      </div>
    </Card>
    <Card
      v-if="documents.length"
      class="fileCard"
      v-bind:class="cardClass"
      v-for="document in documents"
      :no-margin="true"
    >
      <div>
        <slot name="file-icon" :document="document">
          <v-icon class="mr-2" size="18px">mdi-file-outline</v-icon>
        </slot>
        <span @click="onDownload(document)" class="text">{{ getFileName(document) }}</span>
        <v-icon
          v-if="isEdit && (!lastNotDelete || (lastNotDelete && documents.length > 1))"
          @click="onDelete(document)"
          class="uploadIcon"
          color="primary"
          size="18px"
        >
          mdi-close
        </v-icon>
      </div>
    </Card>
    <Card
      v-if="!isEdit && documents.length && downloadAllFiles"
      @click="downloadAllFiles"
      class="fileCard"
      v-bind:class="cardClass"
      :no-margin="true"
    >
      <div>
        <v-icon class="uploadIcon mr-2" color="primary" size="18px">mdi-download-outline</v-icon>
        <span class="text">{{ $t('Скачать всё') }}</span>
      </div>
    </Card>
    <div v-if="!isEdit && documents.length === 0">{{ $t('Отсутствуют') }}</div>
    <v-file-input
      v-if="isEdit"
      :accept="uploadExtensions"
      v-model="files"
      @change="onUpload"
      :loading="uploading"
      v-bind:class="dragOverClass"
      @dragover="dragOver = true"
      @drop="dragOver = false"
      @dragleave="dragOver = false"
      class="uploader"
      label="File input"
      variant="solo"
      prepend-icon=""
      chips
      flat
      multiple
      hide-details
      single-line
    >
      <template v-slot:label>
        <div class="uploadZone">
          <v-icon class="uploadIcon" color="primary" size="58px">mdi-upload</v-icon>
          <br/>
          {{ $t('Перетащите файл сюда') }} <br/>{{ $t('или')}} <span>{{ $t('выберите вручную') }}</span>
        </div>
      </template>
      <template v-slot:selection>
      </template>
      <template v-slot:clear>
      </template>
    </v-file-input>
    <div v-if="errors.length" class="errors">
      <div v-for="error in errors">
        {{ $t(error) }}
      </div>
    </div>

    <ConfirmDialog ref="approve" />
    <v-dialog
      v-model="deletingDialog.show"
      :scrim="false"
      persistent
      width="300"
    >
      <v-card color="primary" dark>
        <v-card-text>
          {{ $t(deletingDialog.text) }}
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
  </Card>
</template>

<script>
import Card from "./Card.vue";
import ConfirmDialog from "./ConfirmDialog.vue";

export default {
  name: 'FileInput',
  components: {ConfirmDialog, Card},
  props: {
    title: {
      type: String,
      default: 'Документы процедуры',
    },
    subtitle: {
      type: String,
      default: ''
    },
    withHtmlSubTitle: {
      type: Boolean,
      default: false,
    },
    titleClass: {
      type: String,
      default: 'cardTitle',
    },
    required: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    uploadExtensions: {
      default: '',
    },
    fileRules: {
      type: Array,
      default: () => [],
    },
    maxCountFiles: {
      type: Number,
      default: 0,
    },
    documents: {
      type: Array,
      default: () => [],
    },
    patterns: {
      default: [],
    },
    downloadFile: {
      type: Function,
      default: () => {}
    },
    downloadPattern: {
      type: Function,
      default: () => {}
    },
    downloadAllFiles: {
      type: Function,
      default: null,
    },
    uploadFile: {
      type: Function,
      default: () => {},
    },
    deleteFile: {
      type: Function,
      default: () => {},
    },
    onError: {
      type: Function,
      default: () => {},
    },
    formatFileInfo: {
      type: Array,
      default: () => [],
    },
    noCard: {
      type: Boolean,
      default: false
    },
    withoutTitle: {
      type: Boolean,
      default: false
    },
    withoutSubTitle: {
      type: Boolean,
      default: false
    },
    rewriteModal: {
      type: Boolean,
      default: false
    },
    lastNotDelete: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      files: [],
      uploading: false,
      errors: [],
      dragOver: false,
      deletingDialog: {
        show: false,
        text: 'Документ удаляется',
      },
    };
  },
  methods: {
    async onDownload(document) {
      await this.downloadFile(document.id)
    },
    getFileName(file) {
      const out = this.formatFileInfo.map(cb => cb(file));
      return out.length ? `${file.fileName} (${out.join(', ')})` : file.fileName
    },
    async onUpload() {
      this.errors = [];

      if (!this.files.length) {
        return;
      }

      let shouldRewrite = null;
      if (this.rewriteModal && this.files.length > 0 && this.documents.length > 0) {
        shouldRewrite = await this.$refs.approve.open(
          'Загрузка документа',
          'Файл будет заменен, вы уверены?',
        );
        if (!shouldRewrite) return;
      }

      const rewrite = this.rewriteModal && shouldRewrite
      if (rewrite && this.files.length > this.maxCountFiles
        || !rewrite && this.maxCountFiles > 0 && this.files.length + this.documents.length > this.maxCountFiles) {
        this.errors.push(this.$t(`Максимальное количество загружаемых файлов: {_1}`, {'_1': this.maxCountFiles}));
        return;
      }

      const validFiles = [];
      const invalidErrors = [];

      for (const file of this.files) {
        let fileCheckResult = true;
        for (const rule of this.fileRules) {
          const result = rule(file);
          if (result !== true) {
            fileCheckResult = result;
            break;
          }
        }

        if (fileCheckResult === true) {
          validFiles.push(file);
        } else {
          invalidErrors.push(fileCheckResult);
        }
      }

      if (invalidErrors.length) {
        this.errors.push(...invalidErrors);
      }

      if (validFiles.length) {
        this.uploading = true;
        await Promise.all(validFiles.map(file => this.uploadFile(file, shouldRewrite)));
        this.uploading = false;
      }

      this.files = [];
    },
    async onDelete(item) {
      if (!this.deleteFile) {
        return;
      }
      let shouldDelete = await this.$refs.approve.open(
        'Удаление документа',
        'Вы действительно хотите удалить документ?',
      );
      if (shouldDelete) {
        try {
          this.deletingDialog.show = true;
          await this.deleteFile(item);
        } catch (e) {
          this.onError(e.message);
        }
        this.deletingDialog.show = false;
      }
    }
  },
  computed: {
    dragOverClass() {
      return this.dragOver ? 'dragOver' : '';
    },
    cardClass() {
      return this.isEdit ? '' : 'fileCardView';
    }
  },
};
</script>
<style scoped>
.pattern {
  background-color: rgb(var(--grey-1));
}

sup {
  color: rgb(var(--error-color));
}

.fileCard {
  border-radius: 5px;
  padding: 10px;
  cursor: pointer;
  color: rgb(var(--primary-color));

  :hover {
    .text {
      text-decoration-line: underline;
    }
  }

  div {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: space-between;
    font-family: var(--primary-font-family-name);
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;

    .text {
      flex-grow: 1;
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }
}

.fileCardView {
  align-self: baseline;
}

.errors {
  font-family: var(--primary-font-family-name);
  font-size: 14px;
  line-height: 14px;
  word-break: break-word;
  overflow-wrap: break-word;
  word-wrap: break-word;
  transition-duration: 150ms;
  color: rgb(var(--error-color));
}

.errors div {
  margin-bottom: 8px;
}

.uploadZone {
  font-family: var(--primary-font-family-name);
  font-weight: 500;
  font-size: 16px;
  line-height: 21px;
  text-align: center;
  color: rgba(var(--primary-color), 0.6);

  span {
    color: rgba(var(--primary-color));
  }

  .uploadIcon {
    padding-bottom: 17px;
  }
}

:deep(.v-field__field) {
  background: transparent;
  border: 1px dashed rgba(var(--primary-color), 0.6);
  border-radius: 10px;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
}

.dragOver :deep(.v-field__field) {
  background: rgba(var(--primary-color), 0.04);
}

:deep(.v-field__field) > label {
  position: relative;
  opacity: 100%;
}

:deep(.v-field--appended) {
  padding: 0 !important;
}

:deep(.v-field--active .v-label.v-field-label) {
  visibility: visible;
}

:deep(.v-field__clearable) {
  margin: 0;
}

.uploader :deep(input) {
  cursor: pointer;
}

.no-card {
  background-color: transparent !important;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
}
</style>
